import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { can } from '../../lib/roles';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  getJob,
  listRankedCandidates,
  submitApplication,
  listCandidates,
  extractJdFromFile,
  updateJob,
  analyzeCV,
  analyzeCVByFileId,
  publishJob,
  getCandidateCvStreamUrl,
  type JobDto,
  type CandidateDto,
  type ApplicationDto,
} from '../../lib/recruitment';
import { listUsers, type UserDto } from '../../lib/users';
import { listDivisions, type DivisionDto } from '../../lib/divisions';
import { listOrgLevels, type OrgLevelDto } from '../../lib/orgLevels';
import { uploadFile } from '../../lib/fileUpload';
import { updateApplicationStatus } from '../../lib/recruitment';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  ArrowLeft, Edit2, Check, X, Upload, Users, Briefcase,
  Sparkles, FileText, Info, AlertCircle, User, Mail, ExternalLink,
  BarChart2, Target,
} from 'lucide-react';
import { toast } from 'sonner';

export const Route = createFileRoute('/rekrut/jobs/$jobId')({
  component: JobDetailPage,
});

function JobDetailPage() {
  return (
    <ProtectedRoute minimumRole="manager">
      <JobDetailContent />
    </ProtectedRoute>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

type BreakdownDetail = {
  competencyCode: string;
  requiredLevel: number;
  candidateLevel: number;
  contribution: number;
};

type Breakdown = {
  details: BreakdownDetail[];
  totalWeight: number;
  matchedWeight: number;
};

type RankedEntry = {
  application: ApplicationDto & { breakdown?: Breakdown };
  candidate: CandidateDto;
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  published: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  draft:     'bg-gray-100 text-gray-600 border-gray-200',
  closed:    'bg-red-100 text-red-600 border-red-200',
};

const APP_STATUS_STYLES: Record<string, string> = {
  new:        'bg-gray-100 text-gray-600',
  screening:  'bg-blue-100 text-blue-700',
  interview:  'bg-purple-100 text-purple-700',
  offer:      'bg-amber-100 text-amber-700',
  hired:      'bg-emerald-100 text-emerald-700',
  rejected:   'bg-red-100 text-red-600',
};

// ─── Match score ring ─────────────────────────────────────────────────────────

function MatchRing({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const color = pct >= 80 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-500' : 'text-red-500';
  return (
    <div className={`text-center ${color}`}>
      <p className="text-lg font-bold tabular-nums leading-none">{pct}%</p>
      <p className="text-[10px] text-muted-foreground font-normal">match</p>
    </div>
  );
}

// ─── Match breakdown modal ────────────────────────────────────────────────────

function BreakdownModal({
  open, onClose, candidateName, matchScore, breakdown,
}: {
  open: boolean;
  onClose: () => void;
  candidateName: string;
  matchScore: number;
  breakdown: Breakdown | null;
}) {
  const pct = Math.round(matchScore * 100);
  const ringColor = pct >= 80 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-500' : 'text-red-500';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <BarChart2 className="w-4 h-4 text-primary" />
            Match Breakdown — {candidateName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Overall score */}
          <div className={`text-center py-3 rounded-xl bg-muted/50 ${ringColor}`}>
            <p className="text-4xl font-bold tabular-nums">{pct}%</p>
            <p className="text-sm text-muted-foreground mt-0.5">Overall Match Score</p>
          </div>

          {breakdown ? (
            <>
              <Separator />
              <div className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Per-Competency Breakdown
                </p>
                {breakdown.details.map((d) => {
                  const compPct = d.requiredLevel > 0
                    ? Math.min(Math.round((d.candidateLevel / d.requiredLevel) * 100), 100)
                    : 100;
                  const met = d.candidateLevel >= d.requiredLevel;
                  const barColor = met ? 'bg-emerald-500' : compPct >= 50 ? 'bg-amber-400' : 'bg-red-400';

                  return (
                    <div key={d.competencyCode} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium font-mono text-foreground">{d.competencyCode}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">
                            L{d.candidateLevel} / L{d.requiredLevel}
                          </span>
                          {met
                            ? <Check className="w-3.5 h-3.5 text-emerald-500" />
                            : <X className="w-3.5 h-3.5 text-red-400" />
                          }
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${barColor}`}
                            style={{ width: `${compPct}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-muted-foreground w-8 text-right tabular-nums">
                          {compPct}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Separator />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Matched weight: <strong className="text-foreground">{breakdown.matchedWeight.toFixed(1)}</strong></span>
                <span>Total weight: <strong className="text-foreground">{breakdown.totalWeight}</strong></span>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No breakdown available. Re-submit the application to generate one.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── CV Upload modal ──────────────────────────────────────────────────────────

function CVUploadModal({
  open, onClose, candidates, companyId, jobId, onApplicationAdded,
}: {
  open: boolean;
  onClose: () => void;
  candidates: CandidateDto[];
  companyId: string;
  jobId: string;
  onApplicationAdded: () => void;
}) {
  const [step, setStep] = useState<'select' | 'cv' | 'preview' | 'done'>('select');
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [rawText, setRawText] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState<CandidateDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setStep('select');
      setSelectedCandidateId('');
      setRawText('');
      setCvFile(null);
      setPreview(null);
      setError('');
    }
  }, [open]);

  async function handleAnalyze() {
    if (!selectedCandidateId) return;
    setAnalyzing(true);
    setError('');

    let res;
    if (cvFile) {
      // Upload file first, then analyze
      const uploadRes = await uploadFile(
        { companyId, uploadedByUserId: undefined, entityType: 'candidate_cv', entityId: selectedCandidateId, file: cvFile },
      );
      if (!uploadRes.success || !uploadRes.data?.file.id) {
        setError(uploadRes.error ?? 'File upload failed');
        setAnalyzing(false);
        return;
      }
      res = await analyzeCVByFileId({ candidateId: selectedCandidateId, companyId, fileId: uploadRes.data.file.id });
    } else if (rawText.trim()) {
      res = await analyzeCV({ candidateId: selectedCandidateId, companyId, rawText });
    } else {
      setError('Paste CV text or upload a file.');
      setAnalyzing(false);
      return;
    }

    setAnalyzing(false);
    if (res.success && res.data?.candidate) {
      setPreview(res.data.candidate);
      setStep('preview');
    } else {
      const msg = res.error ?? 'CV analysis failed';
      setError(msg);
      toast.error(msg);
    }
  }

  async function handleSubmitApplication() {
    if (!selectedCandidateId) return;
    setSubmitting(true);
    setError('');
    const res = await submitApplication({ companyId, jobId, candidateId: selectedCandidateId });
    setSubmitting(false);
    if (res.success) {
      toast.success('Application submitted');
      setStep('done');
      onApplicationAdded();
    } else {
      const msg = res.error ?? 'Failed to submit application';
      setError(msg);
      toast.error(msg);
    }
  }

  const selectedCandidate = candidates.find((c) => c.id === selectedCandidateId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Upload className="w-4 h-4 text-primary" />
            {step === 'done' ? 'Application Submitted' : 'Analyze CV & Apply'}
          </DialogTitle>
        </DialogHeader>

        {step === 'done' ? (
          <div className="text-center py-6 space-y-3">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <Check className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-foreground">
              {selectedCandidate?.name ?? 'Candidate'} has been added to the pipeline.
            </p>
            <p className="text-xs text-muted-foreground">
              Match score has been computed from the updated competency profile.
            </p>
            <Button onClick={onClose} className="mt-2">Close</Button>
          </div>
        ) : step === 'preview' && preview ? (
          <div className="space-y-4">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-xs font-semibold text-emerald-800 mb-2">
                Extracted Competency Profile — {preview.name}
              </p>
              {preview.competencyProfile.length === 0 ? (
                <p className="text-xs text-emerald-700">No competencies extracted. Try a more detailed CV text.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {preview.competencyProfile.map((c) => (
                    <span key={c.code} className="text-xs bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full font-mono">
                      {c.code} · L{c.level}
                      {c.confidence !== undefined && (
                        <span className="text-emerald-500 ml-1">{Math.round(c.confidence * 100)}%</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {error && (
              <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
              </div>
            )}
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('cv')}>Back</Button>
              <Button onClick={handleSubmitApplication} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Application'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Step 1: select candidate */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Candidate</label>
              <select
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={selectedCandidateId}
                onChange={(e) => { setSelectedCandidateId(e.target.value); setStep('cv'); }}
              >
                <option value="">— Select candidate —</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                ))}
              </select>
            </div>

            {selectedCandidateId && (
              <>
                {/* Step 2: paste or upload CV */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Paste CV text
                  </label>
                  <textarea
                    rows={6}
                    placeholder="Paste the candidate's resume text here…"
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
                    value={rawText}
                    onChange={(e) => { setRawText(e.target.value); setCvFile(null); }}
                  />
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="flex-1 border-t" />
                  <span>or upload a file</span>
                  <div className="flex-1 border-t" />
                </div>
                <label className={cn(
                  'flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors',
                  cvFile ? 'border-primary/40 bg-primary/5' : 'border-muted-foreground/20 hover:border-primary/30',
                )}>
                  <input
                    type="file"
                    accept=".pdf,.docx,.doc,.txt"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) { setCvFile(f); setRawText(''); }
                      e.target.value = '';
                    }}
                  />
                  <Upload className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {cvFile ? cvFile.name : 'PDF, DOCX, or TXT'}
                  </span>
                  {cvFile && <button type="button" onClick={(e) => { e.preventDefault(); setCvFile(null); }} className="ml-auto text-muted-foreground hover:text-red-500"><X className="w-3.5 h-3.5" /></button>}
                </label>
              </>
            )}

            {error && (
              <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={handleAnalyze}
                disabled={!selectedCandidateId || (!rawText.trim() && !cvFile) || analyzing}
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                {analyzing ? 'Analyzing CV…' : 'Analyze & Preview'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Main content ─────────────────────────────────────────────────────────────

function JobDetailContent() {
  const { jobId } = Route.useParams() as { jobId: string };
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const canManageJob = can(user?.role, 'manage:rekrut');

  const [job, setJob] = useState<JobDto | null>(null);
  const [ranked, setRanked] = useState<RankedEntry[]>([]);
  const [candidates, setCandidates] = useState<CandidateDto[]>([]);
  const [managers, setManagers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editDept, setEditDept] = useState('');         // free-text fallback (new division)
  const [editDivisionId, setEditDivisionId] = useState('');
  const [editOrgLevelId, setEditOrgLevelId] = useState('');
  const [editHiringMgr, setEditHiringMgr] = useState('');
  const [editNewDivision, setEditNewDivision] = useState(false); // "Starting a new division" toggle
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Org structure lists for dropdowns
  const [divisions, setDivisions] = useState<DivisionDto[]>([]);
  const [orgLevels, setOrgLevels] = useState<OrgLevelDto[]>([]);

  // JD extract state
  const [jdFile, setJdFile] = useState<File | null>(null);
  const [jdExtracting, setJdExtracting] = useState(false);
  const [jdError, setJdError] = useState('');
  const [jdSuccess, setJdSuccess] = useState('');

  // CV upload modal
  const [showCVModal, setShowCVModal] = useState(false);

  // Breakdown modal
  const [breakdown, setBreakdown] = useState<{
    open: boolean;
    candidateName: string;
    matchScore: number;
    data: Breakdown | null;
  }>({ open: false, candidateName: '', matchScore: 0, data: null });

  // Publish/close state
  const [statusChanging, setStatusChanging] = useState(false);

  function handleViewCv(e: React.MouseEvent, candidateId: string) {
    e.stopPropagation();
    const url = getCandidateCvStreamUrl(candidateId, companyId);
    if (!url) { toast.error('Not authenticated'); return; }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  const loadRanked = useCallback(async () => {
    const r = await listRankedCandidates({ companyId, jobId });
    if (r.success && r.data) setRanked(r.data.rankedCandidates as RankedEntry[]);
  }, [companyId, jobId]);

  useEffect(() => {
    Promise.all([
      getJob(jobId),
      listRankedCandidates({ companyId, jobId }),
      listCandidates(companyId),
      listUsers(companyId, {}),
      listDivisions(companyId),
      listOrgLevels(companyId),
    ]).then(([jobRes, rankedRes, cRes, usersRes, divRes, orgRes]) => {
      setLoading(false);
      if (jobRes.success && jobRes.data) {
        const j = jobRes.data.job;
        setJob(j);
        setEditTitle(j.title);
        setEditDesc(j.description ?? '');
        setEditDept(j.department);
        setEditDivisionId(j.divisionId ?? '');
        setEditOrgLevelId(j.orgLevelId ?? '');
        setEditHiringMgr(j.hiringManagerUserId ?? '');
        // If no division FK yet, treat as new-division mode so dept text still shows
        setEditNewDivision(!j.divisionId);
      } else {
        setError('Job not found');
      }
      if (rankedRes.success && rankedRes.data) setRanked(rankedRes.data.rankedCandidates as RankedEntry[]);
      if (cRes.success && cRes.data) setCandidates(cRes.data.candidates);
      if (usersRes.success && usersRes.data) {
        setManagers(usersRes.data.users.filter((u) => ['manager', 'hr', 'admin'].includes(u.role)));
      }
      if (divRes.success && divRes.data) setDivisions(divRes.data.divisions);
      if (orgRes.success && orgRes.data) setOrgLevels(orgRes.data.orgLevels);
    });
  }, [companyId, jobId]);

  async function handleEditSave(e: React.FormEvent) {
    e.preventDefault();
    if (!job) return;
    setEditSaving(true);
    setEditError('');
    // Derive display department: if linked to a division, use its name as fallback
    const divisionName = editNewDivision
      ? editDept
      : (divisions.find((d) => d.id === editDivisionId)?.name ?? editDept);
    const res = await updateJob(jobId, {
      companyId,
      title: editTitle,
      description: editDesc,
      department: divisionName,
      divisionId: editNewDivision ? null : (editDivisionId || null),
      orgLevelId: editOrgLevelId || null,
      hiringManagerUserId: editHiringMgr || null,
    });
    setEditSaving(false);
    if (res.success && res.data) {
      toast.success('Job updated');
      setJob(res.data.job);
      setEditing(false);
    } else {
      const msg = res.error ?? 'Failed to save';
      toast.error(msg);
      setEditError(msg);
    }
  }

  async function handleJdExtract(e: React.FormEvent) {
    e.preventDefault();
    if (!jdFile) return;
    setJdExtracting(true);
    setJdError('');
    setJdSuccess('');
    const uploadRes = await uploadFile(
      { companyId, uploadedByUserId: user?.id, entityType: 'job_description', entityId: jobId, file: jdFile },
    );
    if (!uploadRes.success || !uploadRes.data?.file.id) {
      setJdExtracting(false);
      setJdError(uploadRes.error ?? 'File upload failed');
      return;
    }
    const extractRes = await extractJdFromFile({ jobId, companyId, fileId: uploadRes.data.file.id });
    setJdExtracting(false);
    if (extractRes.success && extractRes.data) {
      const msg = `Extracted ${extractRes.data.count} required competencies`;
      toast.success(msg);
      setJdSuccess(msg + '.');
      setJdFile(null);
      const refreshed = await getJob(jobId);
      if (refreshed.success && refreshed.data) setJob(refreshed.data.job);
    } else {
      const msg = extractRes.error ?? 'Extraction failed';
      toast.error(msg);
      setJdError(msg);
    }
  }

  async function handlePublish() {
    if (!job) return;
    setStatusChanging(true);
    const res = await publishJob({ companyId, jobId });
    setStatusChanging(false);
    if (res.success && res.data) {
      toast.success(job.status === 'published' ? 'Job closed' : 'Job published');
      setJob(res.data.job);
    } else {
      toast.error(res.error ?? 'Failed to update job status');
    }
  }

  async function handleStatusChange(appId: string, status: ApplicationDto['status']) {
    const res = await updateApplicationStatus(appId, status, companyId);
    if (res.success && res.data) {
      toast.success('Application status updated');
      setRanked((prev) =>
        prev.map((r) =>
          r.application.id === appId ? { ...r, application: { ...r.application, status } } : r
        )
      );
    } else {
      toast.error(res.error ?? 'Failed to update status');
    }
  }

  if (loading) return (
    <div className="space-y-4">
      <div className="h-6 w-40 bg-muted rounded animate-pulse" />
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="h-48 bg-muted rounded-xl animate-pulse" />
          <div className="h-32 bg-muted rounded-xl animate-pulse" />
        </div>
        <div className="lg:col-span-3 h-80 bg-muted rounded-xl animate-pulse" />
      </div>
    </div>
  );

  if (error || !job) return (
    <div className="text-center py-12">
      <p className="text-muted-foreground">{error || 'Job not found'}</p>
      <Link to={'/rekrut/jobs' as any} className="text-sm text-primary underline mt-2 block">Back to Jobs</Link>
    </div>
  );

  const hiringManager = managers.find((m) => m.id === (job as any).hiringManagerUserId);

  return (
    <div className="space-y-5">
      {/* Back nav */}
      <Link to={'/rekrut/jobs' as any} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Job Postings
      </Link>

      <div className="grid lg:grid-cols-5 gap-6 items-start">

        {/* ── Left column: job info ──────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">

          {/* Job header card */}
          <div className="bg-card rounded-xl border shadow-sm p-5">
            {editing ? (
              <form onSubmit={handleEditSave} className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Title</label>
                  <input required className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                </div>
                {/* Division + Seniority */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-muted-foreground flex-1">Division</label>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={editNewDivision}
                        onChange={(e) => {
                          setEditNewDivision(e.target.checked);
                          if (!e.target.checked) setEditDept('');
                        }}
                        className="rounded"
                      />
                      Starting a new division
                    </label>
                  </div>
                  {editNewDivision ? (
                    <input
                      required
                      placeholder="New division name…"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={editDept}
                      onChange={(e) => setEditDept(e.target.value)}
                    />
                  ) : (
                    <select
                      required
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      value={editDivisionId}
                      onChange={(e) => setEditDivisionId(e.target.value)}
                    >
                      <option value="">— Select division —</option>
                      {divisions.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Seniority Level</label>
                  <select
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={editOrgLevelId}
                    onChange={(e) => setEditOrgLevelId(e.target.value)}
                  >
                    <option value="">— Any level —</option>
                    {orgLevels.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Description</label>
                  <textarea rows={4} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Hiring Manager</label>
                  <select className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" value={editHiringMgr} onChange={(e) => setEditHiringMgr(e.target.value)}>
                    <option value="">— None —</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                    ))}
                  </select>
                </div>
                {editError && <p className="text-xs text-destructive">{editError}</p>}
                <div className="flex gap-2 pt-1">
                  <Button type="submit" size="sm" disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </form>
            ) : (
              <div>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <h1 className="text-xl font-semibold text-foreground leading-tight">{job.title}</h1>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Briefcase className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm text-muted-foreground">
                        {job.divisionId
                          ? (divisions.find((d) => d.id === job.divisionId)?.name ?? job.department)
                          : job.department}
                      </span>
                      {job.orgLevelId && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 border border-stone-200 font-medium">
                          {orgLevels.find((o) => o.id === job.orgLevelId)?.name ?? ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className={cn('text-xs px-2.5 py-1 rounded-full border capitalize font-medium', STATUS_STYLES[job.status] ?? STATUS_STYLES.draft)}>
                      {job.status}
                    </span>
                    {canManageJob && (
                      <button type="button" onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                        <Edit2 className="w-3 h-3" /> Edit
                      </button>
                    )}
                  </div>
                </div>

                {job.description && (
                  <>
                    <Separator className="my-3" />
                    <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{job.description}</p>
                  </>
                )}

                {hiringManager && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                    <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Hiring Manager</p>
                      <p className="text-xs font-medium text-foreground">{hiringManager.name}</p>
                    </div>
                  </div>
                )}

                {/* Publish / close actions */}
                {canManageJob && (
                  <div className="mt-4 pt-3 border-t flex gap-2">
                    {job.status === 'draft' && (
                      <Button size="sm" onClick={handlePublish} disabled={statusChanging} className="flex-1">
                        {statusChanging ? 'Publishing…' : 'Publish Job'}
                      </Button>
                    )}
                    {job.status === 'published' && (
                      <Button size="sm" variant="outline" onClick={handlePublish} disabled={statusChanging} className="flex-1 text-red-600 border-red-200 hover:bg-red-50">
                        {statusChanging ? '…' : 'Close Job'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Required competencies card */}
          <div className="bg-card rounded-xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Target className="w-4 h-4 text-primary" />
                Required Competencies
              </h2>
              {canManageJob && (
                <label className="flex items-center gap-1 text-xs text-primary cursor-pointer hover:text-primary/80 font-medium">
                  <FileText className="w-3.5 h-3.5" />
                  {jdFile ? jdFile.name.slice(0, 16) + '…' : 'Import JD'}
                  <input
                    type="file"
                    accept=".pdf,.docx,.doc,.txt"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) { setJdFile(f); setJdError(''); setJdSuccess(''); }
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
            </div>

            {job.requiredCompetencies.length > 0 ? (
              <div className="space-y-2.5">
                {job.requiredCompetencies.map((rc) => (
                  <div key={rc.id} className="flex items-center gap-2">
                    <span className="font-mono text-xs font-semibold text-foreground w-32 shrink-0 truncate">{rc.competencyCode}</span>
                    <div className="flex-1">
                      {/* Visual level bar */}
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((lvl) => (
                          <div
                            key={lvl}
                            className={`h-2 flex-1 rounded-sm ${lvl <= rc.minimumLevel ? 'bg-primary' : 'bg-muted'}`}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-[10px] text-muted-foreground">L{rc.minimumLevel}</span>
                      <span className="text-[10px] text-muted-foreground ml-1 opacity-60">w{rc.weight}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                No required competencies defined.{' '}
                {canManageJob && 'Upload a JD document to auto-extract them.'}
              </p>
            )}

            {jdFile && (
              <form onSubmit={handleJdExtract} className="mt-3 space-y-2">
                <div className="flex items-center gap-2 p-2 bg-primary/5 rounded-lg">
                  <FileText className="w-4 h-4 text-primary/60 shrink-0" />
                  <span className="text-xs font-medium text-foreground flex-1 truncate">{jdFile.name}</span>
                  <button type="button" onClick={() => setJdFile(null)} className="text-muted-foreground hover:text-red-500 shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <Button type="submit" size="sm" className="w-full" disabled={jdExtracting}>
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                  {jdExtracting ? 'Analyzing JD…' : 'Extract Competencies with AI'}
                </Button>
              </form>
            )}
            {jdError && <p className="text-xs text-destructive mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{jdError}</p>}
            {jdSuccess && <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1"><Check className="w-3 h-3" />{jdSuccess}</p>}
          </div>
        </div>

        {/* ── Right column: candidates ───────────────────────────────────── */}
        <div className="lg:col-span-3">
          <div className="bg-card rounded-xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Users className="w-4 h-4 text-primary" />
                Candidates
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  ({ranked.length} ranked by match)
                </span>
              </h2>
              {canManageJob && (
                <Button size="sm" onClick={() => setShowCVModal(true)}>
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Add Candidate + CV
                </Button>
              )}
            </div>

            {ranked.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-20" />
                <p className="text-sm font-medium text-foreground/60">No candidates yet</p>
                <p className="text-xs mt-1">Use "Add Candidate + CV" to analyze a CV and submit an application.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {ranked.map(({ application, candidate }, idx) => (
                  <div
                    key={application.id}
                    className="flex items-center gap-3 rounded-lg border bg-background px-4 py-3 hover:bg-accent/40 transition-colors"
                  >
                    {/* Rank */}
                    <span className="text-sm font-bold text-muted-foreground/60 w-5 text-center shrink-0">
                      {idx + 1}
                    </span>

                    {/* Candidate info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground truncate">{candidate.name}</p>
                        {candidate.competencyProfile.length > 0 && (
                          <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full shrink-0">
                            {candidate.competencyProfile.length} competencies
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Mail className="w-3 h-3 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground truncate">{candidate.email}</p>
                      </div>
                    </div>

                    {/* Match score — clickable for breakdown */}
                    <button
                      type="button"
                      onClick={() => setBreakdown({
                        open: true,
                        candidateName: candidate.name,
                        matchScore: application.matchScore,
                        data: application.breakdown ?? null,
                      })}
                      className="shrink-0 group flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg hover:bg-primary/10 transition-colors"
                      title="Click to see breakdown"
                    >
                      <MatchRing score={application.matchScore} />
                      <span className="text-[9px] text-muted-foreground group-hover:text-primary transition-colors flex items-center gap-0.5">
                        <Info className="w-2.5 h-2.5" /> breakdown
                      </span>
                    </button>

                    {/* Status dropdown */}
                    <select
                      value={application.status}
                      onChange={(e) => handleStatusChange(application.id, e.target.value as ApplicationDto['status'])}
                      className={cn(
                        'text-xs px-2 py-1 rounded-full border-0 font-medium cursor-pointer shrink-0',
                        APP_STATUS_STYLES[application.status] ?? 'bg-muted text-muted-foreground',
                      )}
                    >
                      {(['new', 'screening', 'interview', 'offer', 'hired', 'rejected'] as const).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>

                    {/* View CV button — active if CV exists, greyed out if not */}
                    {candidate.cvUrl ? (
                      <button
                        type="button"
                        onClick={(e) => handleViewCv(e, candidate.id)}
                        className="shrink-0 text-muted-foreground hover:text-blue-600 transition-colors"
                        title="View candidate CV"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    ) : (
                      <span className="shrink-0 text-muted-foreground/30 cursor-default" title="No CV on file">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </span>
                    )}
                    {/* Link to candidate detail */}
                    <button
                      type="button"
                      onClick={() => navigate({ to: '/rekrut/candidates/$candidateId' as any, params: { candidateId: candidate.id } as any })}
                      className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
                      title="View candidate profile"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CV Upload modal */}
      <CVUploadModal
        open={showCVModal}
        onClose={() => setShowCVModal(false)}
        candidates={candidates}
        companyId={companyId}
        jobId={jobId}
        onApplicationAdded={loadRanked}
      />

      {/* Breakdown modal */}
      <BreakdownModal
        open={breakdown.open}
        onClose={() => setBreakdown((b) => ({ ...b, open: false }))}
        candidateName={breakdown.candidateName}
        matchScore={breakdown.matchScore}
        breakdown={breakdown.data}
      />
    </div>
  );
}
